<template>
    <div class="portuna-custom-scrollbar"
        @wheel="onWheel" @touchstart="onTouchstart" @touchmove="onTouchmove">
        <div class="portuna-custom-scrollbar-content" :style="scrollPosition" ref="content">
            <slot/>
        </div>

        <div class="portuna-custom-scrollbar-wrapper portuna-custom-scrollbar-x" v-if="isScrollableHorizontal">
            <div class="portuna-custom-scrollbar-handler" data-axis="x" :style="horizontalBarStyle"
                @mousedown="handlerMousedown">&nbsp;
            </div>
        </div>
        <div class="portuna-custom-scrollbar-wrapper portuna-custom-scrollbar-y" v-if="isScrollableVertical">
            <div class="portuna-custom-scrollbar-handler" data-axis="y" :style="verticalBarStyle"
                @mousedown="handlerMousedown">&nbsp;
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator';
    import {unwatchResize, watchResize} from '../library/onResize';

    @Component({
        name: 'AppScrollbar',
        emits: ['mounted'],
    })
    export default class AppScrollbar extends Vue
    {
        readonly $refs: {
            content: HTMLElement
        };

        containerHeight = 0;

        containerWidth = 0;

        innerHeight = 0;

        innerWidth = 0;

        innerScrollHeight = 0;

        innerScrollWidth = 0;

        horizontalScrollablePercentage = 0;

        verticalScrollablePercentage = 0;

        currentLeftPosition = 0;

        currentTopPosition = 0;

        containerObserver: PortUNAResizeObserver;

        innerObserver: PortUNAResizeObserver;

        calculateInProgress = true;

        startX = 0;

        startY = 0;

        scrollEventTarget: Nullable<HTMLElement> = null;

        get scrollPosition()
        {
            if (this.calculateInProgress)
            {
                return {};
            }

            return {
                position: 'relative',
                top: (this.currentTopPosition * -1) + 'px',
                left: (this.currentLeftPosition * -1) + 'px',
            };
        }

        get horizontalBarStyle()
        {
            if (this.calculateInProgress)
            {
                return {};
            }

            return {
                width: (this.containerWidth * this.horizontalScrollablePercentage) + 'px',
                left: this.currentLeftPosition * this.horizontalScrollablePercentage + 'px',
            };
        }

        get verticalBarStyle()
        {
            if (this.calculateInProgress)
            {
                return {};
            }

            return {
                height: (this.containerHeight * this.verticalScrollablePercentage) + 'px',
                top: this.currentTopPosition * this.verticalScrollablePercentage + 'px',
            };
        }

        get isScrollableHorizontal()
        {
            return this.containerWidth < this.innerScrollWidth;
        }

        get isScrollableVertical()
        {
            return this.containerHeight < this.innerScrollHeight;
        }

        mounted()
        {
            this.containerObserver = watchResize(this.$el as HTMLElement, this.recalculateDimension);
            this.recalculateDimension();
            this.$emit('mounted');
        }

        unmounted()
        {
            unwatchResize(this.containerObserver);
            this.innerObserver && unwatchResize(this.innerObserver);
        }

        updated()
        {
            if (!this.innerObserver && this.$refs.content)
            {
                this.innerObserver = watchResize(this.$refs.content as HTMLElement, this.recalculateDimension, null, true);
                this.recalculateDimension();
            }
        }

        getDeltaFromEvent(e: WheelEvent)
        {
            let deltaX = e.deltaX;
            let deltaY = e.deltaY;

            if (typeof deltaX === 'undefined' || typeof deltaY === 'undefined')
            {
                // OS X Safari
                //@ts-ignore
                deltaX = e.wheelDeltaX / 6;
                //@ts-ignore
                deltaY = e.wheelDeltaY / 6;
            }

            if (e.deltaMode && e.deltaMode === 1)
            {
                // Firefox in deltaMode 1: Line scrolling
                deltaX *= 10;
                deltaY *= 10;
            }

            if (deltaX !== deltaX && deltaY !== deltaY /* NaN checks */)
            {
                // IE in some mouse drivers
                deltaX = 0;
                //@ts-ignore
                deltaY = e.wheelDelta;
            }

            if (e.shiftKey)
            {
                // reverse axis with shift key
                return [-deltaY, -deltaX];
            }

            return [deltaX, deltaY];
        }

        onTouchstart(event: TouchEvent)
        {
            if (event.touches.length)
            {
                this.startX = event.touches[0].pageX;
                this.startY = event.touches[0].pageY;
            }
        }

        onTouchmove(event: TouchEvent)
        {
            if (event.touches.length)
            {
                const toScrollLeft = event.touches[0].pageX - this.startX;
                const toScrollTop  = event.touches[0].pageY - this.startY;
                this.startX        = event.touches[0].pageX;
                this.startY        = event.touches[0].pageY;

                // reverse values (if we swipe up, we want scroll down)
                this.doScroll(-toScrollLeft, -toScrollTop, event);
            }

            return true;
        }

        handlerMousedown(event: MouseEvent)
        {
            this.startX = event.pageX;
            this.startY = event.pageY;

            if (event.target instanceof HTMLElement)
            {
                this.scrollEventTarget = event.target;
                this.scrollEventTarget.classList.add('portuna-custom-scrollbar-handler-active');
                this.$el.classList.add('portuna-custom-scrollbar-active');
            }

            document.addEventListener('mousemove', this.handlerMousemove);
            document.addEventListener('mouseup', this.handlerMouseup);
        }

        handlerMousemove(event: MouseEvent)
        {
            // mouse out of window or something else is wrong
            if (!(event.target instanceof HTMLElement) || !this.scrollEventTarget)
            {
                return;
            }

            const axis = this.scrollEventTarget.getAttribute('data-axis');

            let toScrollLeft = 0;
            let toScrollTop  = 0;

            if ('x' === axis)
            {
                toScrollLeft = event.pageX - this.startX;
            }
            else
            {
                toScrollTop = event.pageY - this.startY;
            }

            this.startX = event.pageX;
            this.startY = event.pageY;

            this.doScroll(toScrollLeft / this.horizontalScrollablePercentage, toScrollTop / this.verticalScrollablePercentage, event);

            return true;
        }

        handlerMouseup()
        {
            document.removeEventListener('mousemove', this.handlerMousemove);
            document.removeEventListener('mouseup', this.handlerMouseup);

            this.scrollEventTarget?.classList.remove('portuna-custom-scrollbar-handler-active');
            this.$el.classList.remove('portuna-custom-scrollbar-active');

            return true;
        }

        onWheel(event: WheelEvent)
        {
            let [toScrollLeft, toScrollTop] = this.getDeltaFromEvent(event);
            this.doScroll(toScrollLeft, toScrollTop, event);
        }

        doScroll(toScrollLeft: number, toScrollTop: number, event: Event)
        {
            // event not cancelable - do nothing (page was scrolling @ touch event)
            if (!event.cancelable)
            {
                return false;
            }

            // we are already at start/end -> give the scroll event to parent
            if (
                (
                    toScrollLeft === 0
                    || toScrollLeft > 0 && this.currentLeftPosition + this.containerWidth >= this.innerScrollWidth
                    || toScrollLeft < 0 && this.currentLeftPosition === 0
                )
                && (
                    toScrollTop === 0
                    || toScrollTop > 0 && this.currentTopPosition + this.containerHeight >= this.innerScrollHeight
                    || toScrollTop < 0 && this.currentTopPosition === 0
                )
            )
            {
                return false;
            }

            event.stopPropagation();
            event.preventDefault();

            let top  = this.currentTopPosition + toScrollTop;
            let left = this.currentLeftPosition + toScrollLeft;

            // ensure we're not scrolled "to much"
            top  = Math.max(0, top);
            left = Math.max(0, left);
            top  = Math.min(this.innerScrollHeight - this.containerHeight, top);
            left = Math.min(this.innerScrollWidth - this.containerWidth, left);

            this.currentTopPosition  = top;
            this.currentLeftPosition = left;

            return true;
        }

        scrollTo(left: number, top: number)
        {
            this.currentLeftPosition = left;
            this.currentTopPosition  = top;
        }

        scrollLeft(left: number)
        {
            this.scrollTo(left, 0);
        }

        scrollTop(top: number)
        {
            this.scrollTo(0, top);
        }

        recalculateDimension()
        {
            this.calculateInProgress = true;
            if (!this.$refs.content)
            {
                this.calculateInProgress = false;
                return;
            }

            this.$nextTick(() =>
            {
                if (!this.$refs.content)
                {
                    this.calculateInProgress = false;
                    return;
                }

                this.containerHeight = (this.$el as HTMLElement).offsetHeight;
                this.containerWidth  = (this.$el as HTMLElement).offsetWidth;

                const scrollHeight = this.$refs.content.scrollHeight;
                const scrollWidth  = this.$refs.content.scrollWidth;

                this.innerHeight = scrollHeight > this.containerHeight ? this.containerHeight : scrollHeight;
                this.innerWidth  = scrollWidth > this.containerWidth ? this.containerWidth : scrollWidth;

                this.innerScrollHeight = scrollHeight;
                this.innerScrollWidth  = scrollWidth;

                this.horizontalScrollablePercentage = this.containerWidth / this.innerScrollWidth;
                this.verticalScrollablePercentage   = this.containerHeight / this.innerScrollHeight;

                this.calculateInProgress = false;
            });
        }
    }
</script>